import {
  pgTable,
  text,
  integer,
  timestamp,
  jsonb,
  numeric,
  uuid as pgUuid,
  date as pgDate,
} from "drizzle-orm/pg-core";
import { createInsertSchema, createSelectSchema } from "drizzle-zod";
import { z } from "zod";

// Users Table
export const users = pgTable("users", {
  id: integer().primaryKey().generatedAlwaysAsIdentity(),
  username: text("username").unique().notNull(),
  password: text("password").notNull(),
  role: text("role").notNull().default("user"),
  created_at: timestamp("created_at").defaultNow(),
});

// Agency Table
export const agency = pgTable("agency", {
  id: integer().primaryKey().generatedAlwaysAsIdentity(),
  name: text("name").notNull(),
  email: text("email").notNull(),
  phone: text("phone"),
  commissionPercentage: numeric("commissionPercentage").$type<number>(),
  status: text("status").notNull().default("active"),
  created_at: timestamp("created_at").defaultNow(),
});

// Vendors Table
export const vendors = pgTable("vendors", {
  id: integer().primaryKey().generatedAlwaysAsIdentity(),
  name: text("name").notNull(),
  email: text("email").notNull(),
  phone: text("phone"),
  websiteURL: text("websiteURL"),
  industry: text("industry"),
  address: text("address"),
  status: text("status").notNull().default("active"),
  requiredSkills: text("requiredSkills"),
  contractStartDate: text("contractStartDate"),
  contractEndDate: text("contractEndDate"),
  commissionPercentage: numeric("commissionPercentage").$type<number>(),
  relationshipManager: text("relationshipManager"),
  created_at: timestamp("created_at").defaultNow(),
  agency_id: pgUuid("agency_id").notNull().references(() => agency.id),

});

// Projects Table
export const projects = pgTable("projects", {
  id: integer().primaryKey().generatedAlwaysAsIdentity(),
  title: text("title").notNull(),
  vendor_id: integer("vendor_id").notNull().references(() => vendors.id),
  location: text("location"),
  job_type: text("job_type"),
  description: text("description"),
  total_positions: integer("total_positions"),
  min_experience_required: integer("min_experience_required"),
  skills_required: text("skills_required").array(),
  salary_range: text("salary_range"),
  contact_person: text("contact_person"),
  contact_person_email: text("contact_person_email"),
  job_posted_date: text("job_posted_date"),
  job_apply_by_date: text("job_apply_by_date"),
  status: text("status").notNull().default("active"),
  created_at: timestamp("created_at").defaultNow(),
  hr_id: pgUuid("hr_id").notNull().references(() => hr.id),
  notice_period: text("notice_period"),

});

// Candidates Table
export const candidates = pgTable("candidates", {
  id: integer().primaryKey().generatedAlwaysAsIdentity(),
  name: text("name").notNull(),
  email: text("email").notNull(),
  phone: text("phone"),
  location: text("location"),
  skills: text("skills").array(),
  experience: text("experience"),
  source: text("source").notNull().default("internal"),
  notice_period: text("notice_period"),
  status: text("status").notNull().default("new"),
  linkedInUrl: text("linkedInUrl"),
  created_at: timestamp("created_at").defaultNow(),
  current_ctc: text("current_ctc"),
  expected_ctc: text("expected_ctc"),
});

// Project Candidate Table
export const projectCandidate = pgTable("project_candidate", {
  id: pgUuid("id").primaryKey().defaultRandom(),
  created_at: timestamp("created_at").defaultNow(),
  project_id: pgUuid("project_id").notNull().references(() => projects.id),
  status: text("status").notNull(),
  updated_time: timestamp("updated_time").defaultNow(),
  offered_salary: text("offered_salary"),
  data_status: text("data_status"),
  joining_date: pgDate("joining_date"),
  candidate_id: pgUuid("candidate_id").notNull().references(() => candidates.id),
});

//  hr Table
export const hr = pgTable("hr", {
  id: pgUuid("id").primaryKey().defaultRandom(),
  created_at: timestamp("created_at").defaultNow(),
  name: text("name").notNull(),
  email: text("email").notNull(),
  phone: text("phone"),
});

// Insert and Select Schemas for Users
export const insertHrSchema = createInsertSchema(hr);
export const selectHrSchema = createSelectSchema(hr);
export type Hr = z.infer<typeof selectHrSchema>;

// Insert and Select Schemas for Users
export const insertUserSchema = createInsertSchema(users);
export const selectUserSchema = createSelectSchema(users);
export type User = z.infer<typeof selectUserSchema>;

// Insert and Select Schemas for Vendors
export const insertVendorSchema = createInsertSchema(vendors);
export const selectVendorSchema = createSelectSchema(vendors);
export type Vendor = z.infer<typeof selectVendorSchema>;

// Insert and Select Schemas for Agency
export const insertAgencySchema = createInsertSchema(agency);
export const selectAgencySchema = createSelectSchema(agency);
export type Agency = z.infer<typeof selectAgencySchema>;

// Insert and Select Schemas for Projects
export const insertProjectSchema = createInsertSchema(projects);
export const selectProjectSchema = createSelectSchema(projects);
export type Project = z.infer<typeof selectProjectSchema>;

// Insert and Select Schemas for Candidates
export const insertCandidateSchema = createInsertSchema(candidates);
export const selectCandidateSchema = createSelectSchema(candidates);
export type Candidate = z.infer<typeof selectCandidateSchema>;

// Insert and Select Schemas for Project Candidate
export const insertProjectCandidateSchema = createInsertSchema(projectCandidate); 
export const selectProjectCandidateSchema = createSelectSchema(projectCandidate);
export type ProjectCandidate = z.infer<typeof selectProjectCandidateSchema>;
